import { getErrorType } from '@src/utils/api-helpers';
import { useState, useCallback } from 'react';
import { AxiosError } from 'axios';
import {
  setSelectedItems,
  setIsMultiAddressReturn,
  setReturnShipments,
  setPolicyValidatorResponse,
  useReturnStore,
  setFraudDecisions,
} from '@src/stores/returnStore';
import {
  generatePolicyValidatorRequest,
  generatePolicyValidatorVerifyObject,
  generateShippingRequestObject,
  PolicyValidatorErrors,
  generateInStoreShipment,
  checkForIneligibleItems,
} from '@src/utils';
import {
  IPolicyValidatorRequest,
  IReturnEligibilityResponse,
} from '@src/models/api';
import { PolicyValidatorAPI } from '@src/api/returns';

import {
  processPolicyValidatorShipments,
  processAprissVerifyDecision,
} from '@src/utils/itemsToReturnUtils';
import {
  TagPolicyValidatorApiError,
  TagPolicyValidatorMultiAddress,
} from '@src/utils/analytics-tagging';
import {
  ItemToReturn,
  ReturnsFraudPrompt,
  OrderDetailsSessionDataV2,
} from '@src/models/returns';
import { ReturnFraudType } from '@src/constants/analytics';
import { IGenericError } from '@src/models/errors';
import { RETURN_METHOD } from '@src/constants/general';
import { getUniqueMethods } from '@src/utils/general';
import { setReturnMethods } from '@src/stores/newFlowStore';
import { getUserId, getSvocId } from '@src/utils/user';

interface UseProcessValidatorProps {
  orderDetailsV2: OrderDetailsSessionDataV2 | undefined;
  returnEligibilityDetails: IReturnEligibilityResponse | undefined;
  isMilitaryExchangeGuest: boolean;
  proceedToNextStep: () => void;
}

export function useProcessValidator({
  orderDetailsV2,
  returnEligibilityDetails,
  isMilitaryExchangeGuest,
  proceedToNextStep,
}: UseProcessValidatorProps) {
  const [isPolicyValidatorLoading, setIsPolicyValidatorLoading] =
    useState(false);
  const [policyValidatorAlert, setPolicyValidatorAlert] =
    useState<PolicyValidatorErrors>(PolicyValidatorErrors.NONE);
  const currentShipments = useReturnStore((state) => state.returnShipments);

  // APRISS Fraud Modals
  const [renderModal, setRenderModal] = useState<ReturnsFraudPrompt>();
  const [returnFraudType, setReturnFraudType] = useState<
    ReturnFraudType | undefined
  >();

  const closeModal = () => {
    setRenderModal(undefined);
  };
  const processPolicyValidator = useCallback(
    async (selectedItems: ItemToReturn[]) => {
      try {
        if (!orderDetailsV2) throw new Error('No Order Details');
        if (!returnEligibilityDetails)
          throw new Error('No Return Eligibility Response');

        const orderId = orderDetailsV2.orderNumber;
        const { et } = returnEligibilityDetails;
        // Filter out in-store items for policy validator call, throw error if no return method
        const modifiedSelectedItems = selectedItems
          .filter((item) => item.returnMethod !== RETURN_METHOD.IN_STORE)
          .map((item: ItemToReturn) => {
            if (!item.returnMethod)
              throw new Error('Return Method is missing for one or more items');
            return {
              ...item,
              returnMethod: item.returnMethod,
            };
          });

        const ssrHrtvReqLineItem = generatePolicyValidatorRequest(
          modifiedSelectedItems,
          orderDetailsV2.deliveryDictionary,
        );
        const shippingRequest = generateShippingRequestObject(
          orderDetailsV2,
          modifiedSelectedItems,
        );
        const verifyObject = generatePolicyValidatorVerifyObject(
          orderDetailsV2,
          returnEligibilityDetails,
          modifiedSelectedItems,
          shippingRequest,
        );
        const request: IPolicyValidatorRequest = {
          verify: verifyObject,
          ssrHrtvRequest: {
            et,
            ssrHrtvReqLineItem,
            orderId,
            emailId: orderDetailsV2.emailId,
            orderDate: orderDetailsV2.createDateTime,
            userId: getUserId() || '',
            svocId: getSvocId() || '',
          },
          isGuest: isMilitaryExchangeGuest,
          net: orderDetailsV2.net || '',
        };
        setIsPolicyValidatorLoading(true);

        const response = await PolicyValidatorAPI(request);
        const policyValidatorResponse = response.data;
        const { status, ssrHRTVResponse, multiVerifyDecision } =
          policyValidatorResponse?.returnService || {};
        if (!policyValidatorResponse) {
          throw new Error('PolicyValidatorAPI: Empty 2xx Response');
        }

        const isValidNetworkResponse = status === 'success' && ssrHRTVResponse;
        if (!isValidNetworkResponse) {
          throw new Error(
            'PolicyValidatorAPI: Invalid PolicyValidator Response',
          );
        }

        const hasIneligibleItem = checkForIneligibleItems(
          ssrHRTVResponse.shipments,
        );

        // for multiVerifyDecision
        const fraudType = processAprissVerifyDecision(
          hasIneligibleItem,
          setReturnFraudType,
          setRenderModal,
          closeModal,
          proceedToNextStep,
          setFraudDecisions,
          multiVerifyDecision,
        );

        if (hasIneligibleItem && !fraudType) {
          // throw error, unless fraud modal is being shown
          throw new Error('HAS_INELIGIBLE_ITEMS');
        }

        setIsPolicyValidatorLoading(false);
        setPolicyValidatorAlert(PolicyValidatorErrors.NONE);
        // Rewrite this to include all types of returns, not just multi-address, start by removing uses and then the isMultiAddress flag
        // Use current shipments from store to preserve user-entered states
        const modifiedReturnShipments = processPolicyValidatorShipments(
          policyValidatorResponse,
          TagPolicyValidatorMultiAddress,
          currentShipments,
        );
        // Build and append a new shipments object for in-store returns if necessary
        const hasInStoreReturn = selectedItems.some(
          (item) => item.returnMethod === RETURN_METHOD.IN_STORE,
        );

        if (hasInStoreReturn) {
          const inStoreShipment = generateInStoreShipment(
            selectedItems,
            modifiedReturnShipments,
          );
          modifiedReturnShipments.push(inStoreShipment);
        }

        // Will probably need to set returnShipments here to prevent race condition with processPolicyValidatorShipments
        setReturnShipments(modifiedReturnShipments);
        // updates return methods to the shipments
        setReturnMethods(getUniqueMethods(selectedItems));

        setPolicyValidatorResponse(ssrHRTVResponse);
        return policyValidatorResponse;
      } catch (error: unknown) {
        const axiosError = error as AxiosError<IGenericError>;
        // We don't want an additional tag for partial item eligibility errors here, as they were already tagged in the policyValidator call
        if (axiosError?.message !== 'PARTIAL_INELIGIBLE_ITEMS') {
          // Tagging Analytics when an error is thrown
          TagPolicyValidatorApiError(axiosError);
        }
        setIsPolicyValidatorLoading(false);
        const errorType = getErrorType(axiosError);
        setPolicyValidatorAlert(errorType);
        return undefined;
      }
    },
    [
      orderDetailsV2,
      returnEligibilityDetails,
      isMilitaryExchangeGuest,
      setSelectedItems,
      setReturnFraudType,
      setRenderModal,
      closeModal,
      proceedToNextStep,
      setIsPolicyValidatorLoading,
      setPolicyValidatorAlert,
      setIsMultiAddressReturn,
      setReturnShipments,
      setPolicyValidatorResponse,
    ],
  );

  return {
    processPolicyValidator,
    policyValidatorAlert,
    renderModal,
    returnFraudType,
    setPolicyValidatorAlert,
    isPolicyValidatorLoading,
  };
}
